import { AddDiv, CreateDomElement } from '../engine/viewer/domutils.js';
import { ButtonDialog, ProgressDialog } from './dialog.js';
import { FLoc, Loc } from '../engine/core/localization.js';
import { UploadControl } from './apiclient.js';
import { AddCheckbox, AddSelect } from './utils.js';

const DAY = 24 * 60 * 60 * 1000;

// The terms a link can be given. A fixed set rather than a free number: these
// are the answers people actually want, and a field would only invite "0".
const ShareTerms = [null, 1, 7, 30, 90];

// Decimal, to match the numbers written on the price list. A person reading
// "1 ГБ" there and 1 073 741 824 here would reasonably think we are cheating.
// "1.0 ГБ" reads like a rounding artefact of something else; the number
// people expect to see next to a plan called "1 ГБ" is 1 ГБ.
function OneDecimal (value)
{
    let rounded = Math.round (value * 10) / 10;
    return Number.isInteger (rounded) ? String (rounded) : rounded.toFixed (1);
}

export function FormatBytes (bytes)
{
    if (bytes >= 1000 * 1000 * 1000) {
        return FLoc ('{0} GB', OneDecimal (bytes / (1000 * 1000 * 1000)));
    }
    if (bytes >= 1000 * 1000) {
        return FLoc ('{0} MB', Math.round (bytes / (1000 * 1000)));
    }
    // Zero is zero: rounding it up to 1 КБ would make an empty account look
    // like it is already holding something.
    if (bytes === 0) {
        return FLoc ('{0} KB', 0);
    }
    return FLoc ('{0} KB', Math.max (1, Math.round (bytes / 1000)));
}

export function FormatDate (milliseconds)
{
    // Numeric on purpose: month names would come out in whatever language the
    // browser is set to, inside an interface that is Russian throughout.
    return new Date (milliseconds).toLocaleDateString ('ru-RU');
}

function FormatExpiry (expiresAt)
{
    if (expiresAt === null || expiresAt === undefined) {
        return Loc ('Kept without a time limit');
    }
    let left = expiresAt - Date.now ();
    if (left <= 0) {
        return Loc ('Will be removed within the hour');
    }
    if (left < DAY) {
        return Loc ('Less than a day left');
    }
    // Rounded, not floored: a scene saved a second ago has 6 days 23 hours
    // left, and "6 дн." right after a promise of seven looks like a cheat.
    // "дн." rather than "дней" so the line does not need plural forms it
    // cannot get right for every number.
    return FLoc ('{0} d. left', Math.round (left / DAY));
}

// The only warning a person gets before files start disappearing. There is no
// mail on the domain, so it has to be impossible to miss here — it goes at the
// top of both the list of models and the subscription dialog.
export function AddGraceWarning (parentDiv, state)
{
    if (!state || !state.graceUntil) {
        return null;
    }
    let daysLeft = Math.max (0, Math.round ((state.graceUntil - Date.now ()) / DAY));
    let warningDiv = AddDiv (parentDiv, 'ov_billing_warning');
    AddDiv (warningDiv, 'ov_billing_warning_title', Loc ('Storage is over the free limit'));
    AddDiv (warningDiv, 'ov_billing_warning_text', FLoc (
        'Extra {0} is stored. On {1}, in {2} d., the oldest models will be deleted — only as many as it takes to fit the free plan. Renew the subscription or delete what you do not need.',
        FormatBytes (state.overQuotaBytes || 0),
        FormatDate (state.graceUntil),
        daysLeft
    ));
    return warningDiv;
}

// Which scenes the sweep will take when the grace period ends. Mirrors
// enforceGrace on the server exactly — oldest first, and only as many as it
// takes to fit back into the quota. If the two ever disagree, this list lies
// to somebody about their own files, so they are changed together.
function ScenesAtRisk (scenes, state)
{
    let atRisk = new Set ();
    if (!state || !state.graceUntil) {
        return atRisk;
    }
    let used = state.usedBytes;
    let oldestFirst = scenes.slice ().sort ((left, right) => left.createdAt - right.createdAt);
    for (let scene of oldestFirst) {
        if (used <= state.quotaBytes) {
            break;
        }
        atRisk.add (scene.id);
        used -= scene.sizeBytes;
    }
    return atRisk;
}

function AddQuotaLine (parentDiv, state)
{
    if (!state) {
        return null;
    }
    let line = AddDiv (parentDiv, 'ov_storage_quota');
    line.innerText = FLoc ('Used {0} of {1}', FormatBytes (state.usedBytes), FormatBytes (state.quotaBytes));
    let barDiv = AddDiv (parentDiv, 'ov_storage_bar');
    let fillDiv = AddDiv (barDiv, 'ov_storage_bar_fill');
    let share = state.quotaBytes === 0 ? 0 : Math.min (1, state.usedBytes / state.quotaBytes);
    fillDiv.style.width = `${Math.round (share * 100)}%`;
    // Over the quota the bar is full and red: the number above it says the
    // same thing, but a full red bar is what gets noticed.
    if (state.usedBytes > state.quotaBytes) {
        fillDiv.classList.add ('over');
    }
    return line;
}

function ShowMessageDialog (title, text, extraButton)
{
    let dialog = new ButtonDialog ();
    let buttons = [{
        name : Loc ('Close'),
        subClass : extraButton ? 'outline' : undefined,
        onClick () {
            dialog.Close ();
        }
    }];
    if (extraButton) {
        buttons.push ({
            name : extraButton.name,
            onClick () {
                dialog.Close ();
                extraButton.onClick ();
            }
        });
    }
    let contentDiv = dialog.Init (title, buttons);
    dialog.GetContentDiv ().classList.add ('ov_auth_dialog');
    AddDiv (contentDiv, 'ov_storage_message', text);
    dialog.Open ();
    return dialog;
}

export function ShowScenesDialog (storage, callbacks)
{
    let dialog = new ButtonDialog ();
    let contentDiv = dialog.Init (Loc ('My models'), [
        {
            name : Loc ('Close'),
            onClick () {
                dialog.Close ();
            }
        }
    ]);
    dialog.GetContentDiv ().classList.add ('ov_auth_dialog', 'ov_storage_dialog');

    let warningDiv = AddDiv (contentDiv, 'ov_storage_warning_slot');
    let listDiv = AddDiv (contentDiv, 'ov_storage_list ov_thin_scrollbar');
    listDiv.innerText = Loc ('Loading…');
    let footerDiv = AddDiv (contentDiv, 'ov_storage_footer');

    function Refresh ()
    {
        storage.ListScenes ().then ((scenes) => {
            listDiv.innerHTML = '';
            footerDiv.innerHTML = '';
            warningDiv.innerHTML = '';
            AddGraceWarning (warningDiv, storage.state);
            AddQuotaLine (footerDiv, storage.state);
            if (scenes.length === 0) {
                AddDiv (listDiv, 'ov_storage_empty', Loc ('Nothing saved here yet. Open a model and save it to the account.'));
                return;
            }
            let atRisk = ScenesAtRisk (scenes, storage.state);
            for (let scene of scenes) {
                AddSceneRow (scene, atRisk.has (scene.id));
            }
        }).catch ((error) => {
            listDiv.innerHTML = '';
            AddDiv (listDiv, 'ov_auth_message error', error.message);
        });
    }

    function AddSceneRow (scene, atRisk)
    {
        let rowDiv = AddDiv (listDiv, 'ov_storage_row');
        let textDiv = AddDiv (rowDiv, 'ov_storage_row_text');
        AddDiv (textDiv, 'ov_storage_row_name', scene.name);
        // A scene bought under a subscription has no expiry of its own, so
        // without this it would claim to be kept forever on the same screen
        // that warns it is about to go.
        let fate = atRisk
            ? FLoc ('will be deleted on {0}', FormatDate (storage.state.graceUntil))
            : FormatExpiry (scene.expiresAt);
        let metaDiv = AddDiv (textDiv, 'ov_storage_row_meta',
            `${FormatBytes (scene.sizeBytes)} · ${FormatDate (scene.createdAt)} · ${fate}`);
        if (atRisk) {
            metaDiv.classList.add ('at_risk');
        }

        let actionsDiv = AddDiv (rowDiv, 'ov_storage_row_actions');
        let openLink = AddDiv (actionsDiv, 'ov_auth_link', Loc ('Open'));
        openLink.addEventListener ('click', () => {
            dialog.Close ();
            callbacks.onOpenScene (scene);
        });
        let shareLink = AddDiv (actionsDiv, 'ov_auth_link', Loc ('Share'));
        shareLink.addEventListener ('click', () => {
            ShowShareDialog (storage, scene, callbacks);
        });
        let deleteLink = AddDiv (actionsDiv, 'ov_auth_link ov_storage_danger', Loc ('Delete'));
        deleteLink.addEventListener ('click', () => {
            ConfirmDelete (scene);
        });
    }

    function ConfirmDelete (scene)
    {
        let confirmDialog = new ButtonDialog ();
        let confirmContent = confirmDialog.Init (Loc ('Delete the model?'), [
            {
                name : Loc ('Cancel'),
                subClass : 'outline',
                onClick () {
                    confirmDialog.Close ();
                    // Closing this one closed the list behind it, so the list
                    // is opened again rather than leaving an empty screen.
                    ShowScenesDialog (storage, callbacks);
                }
            },
            {
                name : Loc ('Delete'),
                async onClick () {
                    try {
                        await storage.DeleteScene (scene.id);
                        confirmDialog.Close ();
                        ShowScenesDialog (storage, callbacks);
                    } catch (error) {
                        confirmDialog.Close ();
                        ShowMessageDialog (Loc ('Delete the model?'), error.message);
                    }
                }
            }
        ]);
        confirmDialog.GetContentDiv ().classList.add ('ov_auth_dialog');
        AddDiv (confirmContent, 'ov_storage_message',
            FLoc ('«{0}» will be deleted for good, with all its files.', scene.name));
        confirmDialog.Open ();
    }

    Refresh ();
    dialog.Open ();
    return dialog;
}

function TermTitle (days)
{
    if (days === null) {
        return Loc ('No time limit');
    }
    return days === 1 ? Loc ('1 day') : FLoc ('{0} days', days);
}

// The record keeps a date, not a term, so the select shows the shortest term
// that still covers what is left. The line under it says the date itself,
// which is the fact that actually matters.
function TermIndex (expiresAt)
{
    if (!expiresAt) {
        return 0;
    }
    let daysLeft = Math.ceil ((expiresAt - Date.now ()) / DAY);
    for (let index = 1; index < ShareTerms.length; index++) {
        if (daysLeft <= ShareTerms[index]) {
            return index;
        }
    }
    return ShareTerms.length - 1;
}

// A link to one scene: whether it is on, what the address is, and how to turn
// it off. Opened from the list, and it puts the list back when it closes —
// dialogs here replace one another rather than stack.
export function ShowShareDialog (storage, scene, listCallbacks)
{
    let dialog = new ButtonDialog ();
    let contentDiv = dialog.Init (Loc ('Link to the model'), [
        {
            name : Loc ('Close'),
            onClick () {
                dialog.Close ();
                ShowScenesDialog (storage, listCallbacks);
            }
        }
    ]);
    dialog.GetContentDiv ().classList.add ('ov_auth_dialog', 'ov_storage_dialog');

    AddDiv (contentDiv, 'ov_auth_email', scene.name);
    let bodyDiv = AddDiv (contentDiv, 'ov_share_body');
    let message = null;

    function ShowError (text)
    {
        if (message === null) {
            message = AddDiv (contentDiv, 'ov_auth_message error');
        }
        message.innerText = text;
    }

    function Render (share)
    {
        bodyDiv.innerHTML = '';
        if (share === null || share === undefined) {
            // Said before the button is pressed rather than after the server
            // refuses: on the free plan this is not going to work, and finding
            // that out by clicking is a worse way to learn it.
            if (storage.state !== null && storage.state.plan === 'free') {
                AddDiv (bodyDiv, 'ov_storage_message', Loc ('A link to a model is part of the subscription. On the free plan a saved model is visible only to you.'));
                return;
            }
            AddDiv (bodyDiv, 'ov_storage_message', Loc ('Anyone with the link can open this model in the viewer — no account needed. The link works while the model is stored and the subscription is paid.'));
            // Worth saying out loud for a 3D model: a link hands over the
            // files themselves, not just a picture of them.
            AddDiv (bodyDiv, 'ov_billing_note', Loc ('The files can be downloaded from the link the same way the viewer downloads them — do not share what must not leave.'));
            let enableDiv = AddDiv (bodyDiv, 'ov_button ov_billing_request', Loc ('Turn the link on'));
            enableDiv.addEventListener ('click', async () => {
                try {
                    Render (await storage.CreateShare (scene.id));
                } catch (error) {
                    ShowError (error.message);
                }
            });
            return;
        }

        let linkInput = CreateDomElement ('input', 'ov_share_link');
        linkInput.setAttribute ('type', 'text');
        linkInput.setAttribute ('readonly', 'readonly');
        linkInput.value = share.url;
        bodyDiv.appendChild (linkInput);

        let rowDiv = AddDiv (bodyDiv, 'ov_share_actions');
        let copyDiv = AddDiv (rowDiv, 'ov_button ov_share_copy', Loc ('Copy'));
        copyDiv.addEventListener ('click', () => {
            navigator.clipboard.writeText (share.url).then (() => {
                copyDiv.innerText = Loc ('Copied');
            }).catch (() => {
                // Clipboard access can be refused; selecting the text leaves
                // the person one keystroke away rather than stuck.
                linkInput.select ();
            });
        });

        let offLink = AddDiv (rowDiv, 'ov_auth_link ov_storage_danger', Loc ('Turn the link off'));
        offLink.addEventListener ('click', async () => {
            try {
                await storage.RevokeShare (scene.id);
                scene.share = null;
                Render (null);
            } catch (error) {
                ShowError (error.message);
            }
        });

        // Notes on the model travel with it by default — a marked-up model
        // is usually the reason it is being shown to somebody. Internal ones
        // are a switch away, in the same dialog that hands out the address.
        let settingsDiv = AddDiv (bodyDiv, 'ov_share_settings');
        let commentsCheck = AddCheckbox (settingsDiv, 'share_with_comments',
            Loc ('Show the comments on the model'), share.withComments !== false, () => {
                let wanted = commentsCheck.checked;
                storage.CreateShare (scene.id, { withComments : wanted }).then ((updated) => {
                    scene.share = updated;
                }).catch ((error) => {
                    commentsCheck.checked = !wanted;
                    ShowError (error.message);
                });
            });

        // Two settings that bound a token which will outlive the conversation
        // it was sent in. Both are applied to the link that already exists —
        // its address does not change, because it has already been sent.
        async function Change (settings)
        {
            try {
                let updated = await storage.CreateShare (scene.id, settings);
                scene.share = updated;
                Render (updated);
            } catch (error) {
                ShowError (error.message);
            }
        }

        let termDiv = AddDiv (bodyDiv, 'ov_share_setting');
        AddDiv (termDiv, 'ov_share_setting_label', Loc ('Link term'));
        AddSelect (termDiv, ShareTerms.map (TermTitle), TermIndex (share.expiresAt), (index) => {
            Change ({ expiresInDays : ShareTerms[index] });
        });
        if (share.expiresAt) {
            AddDiv (bodyDiv, 'ov_billing_note', FLoc ('Works until {0}', FormatDate (share.expiresAt)));
        }

        let passwordDiv = AddDiv (bodyDiv, 'ov_share_setting');
        AddDiv (passwordDiv, 'ov_share_setting_label', Loc ('Password'));
        let passwordInput = CreateDomElement ('input', 'ov_auth_input ov_share_password');
        passwordInput.setAttribute ('type', 'password');
        passwordInput.setAttribute ('placeholder',
            share.hasPassword ? Loc ('The password is set') : Loc ('without a password'));
        passwordDiv.appendChild (passwordInput);

        function SavePassword ()
        {
            if (passwordInput.value.length === 0) {
                return;
            }
            Change ({ password : passwordInput.value });
        }
        passwordInput.addEventListener ('keydown', (ev) => {
            if (ev.key === 'Enter') {
                ev.preventDefault ();
                SavePassword ();
            }
        });
        let passwordActions = AddDiv (bodyDiv, 'ov_share_actions');
        let saveDiv = AddDiv (passwordActions, 'ov_button ov_share_copy', Loc ('Save'));
        saveDiv.addEventListener ('click', SavePassword);
        if (share.hasPassword) {
            // Changing or removing it also ends every session that was opened
            // with the old one, which is said here rather than discovered.
            let dropDiv = AddDiv (passwordActions, 'ov_auth_link ov_storage_danger', Loc ('Remove the password'));
            dropDiv.addEventListener ('click', () => {
                Change ({ password : null });
            });
        }
        AddDiv (bodyDiv, 'ov_billing_note', share.hasPassword
            ? Loc ('Whoever opens the link types the password once. Changing or removing it closes the link for everyone who has already typed the old one.')
            : Loc ('A password is asked for once, and then the model opens as usual. Without one, everyone with the address gets in.'));

        AddDiv (bodyDiv, 'ov_billing_note', share.views > 0
            ? FLoc ('Opened {0} times. Turning the link off stops it for good: switching sharing back on gives out a new address.', share.views)
            : Loc ('Nobody has opened it yet. Turning the link off stops it for good: switching sharing back on gives out a new address.'));
    }

    Render (scene.share);
    dialog.Open ();
    return dialog;
}

// The other side of a password: somebody was given a link and a word, and
// this is where the word goes. No account is involved here at all.
export function ShowSharePasswordDialog (storage, token, onOpened)
{
    let dialog = new ButtonDialog ();
    let busy = false;
    let contentDiv = dialog.Init (Loc ('Link to the model'), [
        {
            name : Loc ('Cancel'),
            subClass : 'outline',
            onClick () {
                dialog.Close ();
            }
        },
        {
            name : Loc ('Open'),
            onClick () {
                Submit ();
            }
        }
    ]);
    dialog.GetContentDiv ().classList.add ('ov_auth_dialog');

    AddDiv (contentDiv, 'ov_storage_message',
        Loc ('This model is behind a password. Ask whoever sent the link for it.'));
    let passwordInput = CreateDomElement ('input', 'ov_auth_input');
    passwordInput.setAttribute ('type', 'password');
    contentDiv.appendChild (passwordInput);
    let messageDiv = AddDiv (contentDiv, 'ov_auth_message');
    messageDiv.style.display = 'none';

    async function Submit ()
    {
        if (busy || passwordInput.value.length === 0) {
            return;
        }
        busy = true;
        try {
            let scene = await storage.UnlockShare (token, passwordInput.value);
            dialog.Close ();
            onOpened (scene);
        } catch (error) {
            messageDiv.innerText = error.message;
            messageDiv.classList.add ('error');
            messageDiv.style.display = 'block';
            passwordInput.select ();
        } finally {
            busy = false;
        }
    }

    passwordInput.addEventListener ('keydown', (ev) => {
        if (ev.key === 'Enter') {
            ev.preventDefault ();
            Submit ();
        }
    });

    dialog.Open ();
    passwordInput.focus ();
    return dialog;
}

export function ShowSaveSceneDialog (storage, files, mainFileName, callbacks)
{
    let dialog = new ButtonDialog ();
    let busy = false;
    let totalBytes = files.reduce ((sum, file) => sum + file.size, 0);

    let contentDiv = dialog.Init (Loc ('Save to account'), [
        {
            name : Loc ('Cancel'),
            subClass : 'outline',
            onClick () {
                dialog.Close ();
            }
        },
        {
            name : Loc ('Save'),
            onClick () {
                Submit ();
            }
        }
    ]);
    dialog.GetContentDiv ().classList.add ('ov_auth_dialog');

    AddDiv (contentDiv, 'ov_auth_label', Loc ('Scene name'));
    let nameInput = CreateDomElement ('input', 'ov_auth_input');
    nameInput.setAttribute ('type', 'text');
    nameInput.value = mainFileName;
    contentDiv.appendChild (nameInput);

    AddDiv (contentDiv, 'ov_storage_message',
        FLoc ('Files: {0}, together {1}', files.length, FormatBytes (totalBytes)));

    let messageDiv = AddDiv (contentDiv, 'ov_auth_message');
    messageDiv.style.display = 'none';

    // What is left is asked for before anything is sent, so the number shown
    // is the one the server will check against.
    storage.GetState ().then ((state) => {
        AddQuotaLine (contentDiv, state);
    }).catch (() => {
        // Not being able to show the free space is no reason to block a save:
        // the server checks it anyway.
    });

    nameInput.addEventListener ('keydown', (ev) => {
        if (ev.key === 'Enter') {
            ev.preventDefault ();
            Submit ();
        }
    });

    async function Submit ()
    {
        if (busy) {
            return;
        }
        busy = true;
        dialog.Close ();

        // A gigabyte takes minutes, so the progress has to show more than a
        // spinning logo: how far along it is, how much that is in megabytes,
        // and a way out that does not mean closing the tab.
        let control = new UploadControl ();
        let progress = new ProgressDialog ();
        progress.Init (FLoc ('Uploading… {0}%', 0));
        let progressDiv = progress.GetContentDiv ();
        let barDiv = AddDiv (progressDiv, 'ov_storage_bar ov_progress_bar');
        let fillDiv = AddDiv (barDiv, 'ov_storage_bar_fill');
        let detailDiv = AddDiv (progressDiv, 'ov_progress_detail',
            FLoc ('{0} of {1}', FormatBytes (0), FormatBytes (totalBytes)));
        let cancelDiv = AddDiv (progressDiv, 'ov_auth_link ov_progress_cancel', Loc ('Cancel upload'));
        cancelDiv.addEventListener ('click', () => {
            cancelDiv.innerText = Loc ('Stopping…');
            control.Cancel ();
        });
        progress.Open ();

        try {
            let scene = await storage.SaveScene (files, {
                name : nameInput.value,
                mainFileName : mainFileName,
                control : control,
                onResumed : () => {
                    // Said once and left on screen: without it a bar that
                    // starts at 60% looks like a mistake.
                    AddDiv (progressDiv, 'ov_progress_detail',
                        Loc ('Continuing the upload that was interrupted.'));
                },
                onProgress : (sent, total) => {
                    let share = total === 0 ? 1 : Math.min (1, sent / total);
                    progress.SetText (FLoc ('Uploading… {0}%', Math.floor (share * 100)));
                    fillDiv.style.width = `${Math.round (share * 100)}%`;
                    detailDiv.innerText = FLoc ('{0} of {1}', FormatBytes (sent), FormatBytes (total));
                }
            });
            progress.Close ();
            callbacks.onSaved (scene);
            ShowMessageDialog (
                Loc ('Saved to the account'),
                FLoc ('«{0}» is in your account now. {1}', scene.name, FormatExpiry (scene.expiresAt)),
                { name : Loc ('My models'), onClick : callbacks.onShowScenes }
            );
        } catch (error) {
            progress.Close ();
            if (error.code === 'aborted') {
                ShowMessageDialog (Loc ('Save to account'), Loc ('Upload cancelled, nothing was saved.'));
            } else if (error.code === 'unavailable') {
                // What broke is the connection, and the bytes that did arrive
                // are still on the server: saying so turns a failure into a
                // second attempt that costs only what is left.
                ShowMessageDialog (Loc ('Save to account'),
                    `${error.message} ${Loc ('Save it again — the upload will continue from where it stopped.')}`);
            } else {
                ShowMessageDialog (Loc ('Save to account'), error.message);
            }
        } finally {
            busy = false;
        }
    }

    dialog.Open ();
    nameInput.focus ();
    nameInput.select ();
    return dialog;
}

export { ShowMessageDialog };
